import { Loader2, Save, Send } from "lucide-react";
import { useNotify } from "ra-core";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useOrganization } from "../context/OrganizationContext";
import { supabase } from "../providers/supabase/supabase";

export const EmailSettingsPage = () => {
    const { currentOrgId: orgId, currentOrg } = useOrganization();
    const role = currentOrg?.role;
    const notify = useNotify();
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isTesting, setIsTesting] = useState(false);

    // Form state
    const form = useForm({
        defaultValues: {
            provider: "none",
            from_email: "",
            from_name: "",
            reply_to_email: "",
            api_key: "",
        }
    });

    // Fetch settings
    useEffect(() => {
        if (!orgId) return;

        const fetchSettings = async () => {
            setIsLoading(true);
            try {
                const { data, error } = await supabase
                    .from("email_provider_settings")
                    .select("*")
                    .eq("org_id", orgId)
                    .maybeSingle(); // Use maybeSingle as it might not exist yet

                if (error) throw error;

                if (data) {
                    form.reset({
                        provider: data.provider || "none",
                        from_email: data.from_email || "",
                        from_name: data.from_name || "",
                        reply_to_email: data.reply_to_email || "",
                        api_key: data.api_key || "", // Note: real apps might mask this
                    });
                }
            } catch (error) {
                console.error("Error fetching email settings:", error);
                notify("Failed to load email settings", { type: "error" });
            } finally {
                setIsLoading(false);
            }
        };

        fetchSettings();
    }, [orgId, notify, form]);

    const onSubmit = async (values: any) => {
        if (!orgId) return;
        setIsSaving(true);
        try {
            // Check if record exists
            const { data: existing } = await supabase
                .from("email_provider_settings")
                .select("id")
                .eq("org_id", orgId)
                .maybeSingle();

            let error;
            if (existing) {
                const { error: updateError } = await supabase
                    .from("email_provider_settings")
                    .update({
                        provider: values.provider,
                        from_email: values.from_email,
                        from_name: values.from_name,
                        reply_to_email: values.reply_to_email,
                        api_key: values.api_key,
                        updated_at: new Date().toISOString()
                    })
                    .eq("org_id", orgId);
                error = updateError;
            } else {
                const { error: insertError } = await supabase
                    .from("email_provider_settings")
                    .insert({
                        org_id: orgId,
                        provider: values.provider,
                        from_email: values.from_email,
                        from_name: values.from_name,
                        reply_to_email: values.reply_to_email,
                        api_key: values.api_key
                    });
                error = insertError;
            }

            if (error) throw error;
            notify("Email settings saved successfully", { type: "success" });
        } catch (error) {
            console.error("Error saving email settings:", error);
            notify("Failed to save settings", { type: "error" });
        } finally {
            setIsSaving(false);
        }
    };

    const handleTestEmail = async () => {
        const values = form.getValues();
        if (values.provider === "none" || !values.api_key || !values.from_email) {
            notify("Please configure provider, API key and sender email first", { type: "warning" });
            return;
        }

        setIsTesting(true);
        try {
            // Call test-email edge function (we can reuse email-send with a test flag or just send to self)
            const { data: { user } } = await supabase.auth.getUser();
            if (!user?.email) throw new Error("No user email found");

            const { error } = await supabase.functions.invoke("email-send", {
                body: {
                    org_id: orgId,
                    to_email: user.email,
                    subject: "Test Email from Salesfu CRM",
                    body_html: "<p>This is a test email to verify your configuration. If you see this, it works!</p>",
                    body_text: "This is a test email to verify your configuration. If you see this, it works!"
                }
            });

            if (error) throw error;
            notify(`Test email sent to ${user.email}`, { type: "success" });
        } catch (error) {
            console.error("Test email failed:", error);
            const message = error instanceof Error ? error.message : "Unknown error";
            notify(`Test failed: ${message}`, { type: "error" });
        } finally {
            setIsTesting(false);
        }
    };

    if (role !== "owner" && role !== "admin") {
        return (
            <div className="p-4">
                <Card>
                    <CardContent className="pt-6">
                        <p className="text-center text-muted-foreground">
                            Only organization admins can configure email settings.
                        </p>
                    </CardContent>
                </Card>
            </div>
        );
    }

    if (isLoading) {
        return (
            <div className="flex justify-center p-8">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
        );
    }

    const providerStats = form.watch("provider");

    return (
        <div className="max-w-2xl mx-auto mt-8 px-4">
            <Card>
                <CardHeader>
                    <CardTitle>Email Configuration</CardTitle>
                </CardHeader>
                <CardContent>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">

                        <div className="space-y-2">
                            <Label>Email Provider</Label>
                            <Select
                                onValueChange={(val) => form.setValue("provider", val)}
                                defaultValue={form.watch("provider")}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Select a provider" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="none">None (Disabled)</SelectItem>
                                    <SelectItem value="sendgrid">SendGrid</SelectItem>
                                    <SelectItem value="mailgun">Mailgun (Coming Soon)</SelectItem>
                                    <SelectItem value="postmark">Postmark (Coming Soon)</SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Select the third-party service you use to send emails.
                            </p>
                        </div>

                        {providerStats === "sendgrid" && (
                            <div className="border rounded-md p-4 bg-muted/20 space-y-4">
                                <h3 className="font-medium flex items-center gap-2">
                                    <span className="text-xl">📧</span> SendGrid Configuration
                                </h3>

                                <div className="space-y-2">
                                    <Label>API Key</Label>
                                    <Input
                                        type="password"
                                        placeholder="SG.xxxxxxxxxxxxxxxx"
                                        {...form.register("api_key")}
                                    />
                                    <p className="text-xs text-muted-foreground">
                                        Create a restricted API key with "Mail Send" permissions in SendGrid Settings.
                                    </p>
                                </div>
                            </div>
                        )}

                        {providerStats !== "none" && (
                            <div className="space-y-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label>From Name</Label>
                                        <Input
                                            placeholder="Sales Team"
                                            {...form.register("from_name")}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label>From Email</Label>
                                        <Input
                                            placeholder="sales@yourcompany.com"
                                            {...form.register("from_email")}
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label>Reply-To Email (Optional)</Label>
                                    <Input
                                        placeholder="support@yourcompany.com"
                                        {...form.register("reply_to_email")}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="pt-4 flex items-center justify-between border-t">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={handleTestEmail}
                                disabled={isTesting || providerStats === "none"}
                            >
                                {isTesting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
                                Send Test Email
                            </Button>

                            <Button type="submit" disabled={isSaving}>
                                {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                                Save Settings
                            </Button>
                        </div>
                    </form>
                </CardContent>
            </Card>

            {providerStats === "sendgrid" && (
                <div className="mt-8 space-y-4">
                    <h3 className="text-lg font-medium">How to set up SendGrid</h3>
                    <ol className="list-decimal list-inside space-y-2 text-sm text-muted-foreground">
                        <li>Log in to your <a href="https://app.sendgrid.com" target="_blank" className="underline text-primary" rel="noreferrer">SendGrid account</a>.</li>
                        <li>Go to <strong>Settings</strong> → <strong>Sender Authentication</strong> to verify your domain.</li>
                        <li>Go to <strong>Settings</strong> → <strong>API Keys</strong>.</li>
                        <li>Click <strong>Create API Key</strong>.</li>
                        <li>Give it a name (e.g. "Salesfu CRM") and select <strong>Restricted Access</strong>.</li>
                        <li>Scroll down to <strong>Mail Send</strong> and enable full access (toggle right).</li>
                        <li>Click <strong>Create & View</strong>, then copy the key and paste it above.</li>
                    </ol>
                </div>
            )}
        </div>
    );
};

export default EmailSettingsPage;

EmailSettingsPage.path = "/settings/email";
